/* eslint-disable @typescript-eslint/return-await */

import { MAIN_URL } from 'api/config';

type PendingReq = {
    [key: string]: {
        options: RequestInit;
        retries: number;
    };
};

const pendingRequests: PendingReq = {};
const MAX_RETRIES = 5;
const INTERVAL_MS = 2000;

/**
 *
 * @description Adds or removes request to pending. Helps tracking amount of retries.
 */
const getRetriesCount = (url: string, options: RequestInit = {}): number => {
    // Case:
    // First time caught `failed to fetch`
    if (!pendingRequests[url]) {
        pendingRequests[url] = {
            options,
            retries: 1,
        };

        return 0;
    }

    const currentAttempt = pendingRequests[url].retries;

    // Case:
    // Retried more times than needed - this should stop furter retries.
    if (currentAttempt >= MAX_RETRIES) {
        delete pendingRequests[url];
        return MAX_RETRIES;
    }

    // Default case:
    // Retried less than MAX_RETRIES. Retrying once again and trying again.
    pendingRequests[url] = {
        ...pendingRequests[url],
        retries: currentAttempt + 1,
    };

    return currentAttempt;
};

/**
 *
 * @description Guard cathes Unauthorized exceptions and tries to refresh tokens + resend req.
 *              If tokens were not successfully refreshed - guard logs user out of the app.
 */
export const withErrorGuard = async <ResponseType>(
    url: string,
    options: RequestInit = {},
): Promise<ResponseType> => {
    try {
        const response = await fetch(url, options);
        const result = await response.json();

        if (response.status >= 400) {
            throw result;
        }

        return result;
    } catch (err) {
        if (url === `${MAIN_URL}/accounts/login`) throw err;

        console.log('withErrorGuard err -> ', url);
        console.log('options -> ', options);
        console.log(err);

        if (err.stack === 'TypeError: Failed to fetch') {
            const retries = getRetriesCount(url, options);

            if (retries === MAX_RETRIES) {
                console.log('Request failed too many times. Aborting...');
                return err;
            }

            console.log(`Request to ${url} failed`);
            console.log(`Reason: ${err.stack}`);
            console.log(`Retrying (${retries + 1}) in ${INTERVAL_MS} ms...`);

            setTimeout(() => {
                withErrorGuard(url, options);
            }, INTERVAL_MS);
        } else if (err.statusCode === 429) {
            throw err;
        }

        // If nothing helped - throwing error further to be handled by the caller
        throw err;
    }
};
